import React from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { format, set } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import {
    useApplicationSettings,
    useUpdateApplicationSettings,
} from "@/api/applicationQueries"
import { Timestamp } from "firebase/firestore"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { toast } from "react-hot-toast"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

export function ApplicationDeadlineSettings() {
    const [date, setDate] = React.useState<Date>()
    const [selectedHour, setSelectedHour] = React.useState<string>("12")
    const [selectedMinute, setSelectedMinute] = React.useState<string>("00")
    const [selectedPeriod, setSelectedPeriod] = React.useState<"AM" | "PM">(
        "PM"
    )
    const { data: settings, isLoading } = useApplicationSettings()
    const updateSettings = useUpdateApplicationSettings()

    React.useEffect(() => {
        if (settings?.deadline) {
            const deadlineDate = settings.deadline.toDate()
            setDate(deadlineDate)
            setSelectedHour(format(deadlineDate, "h"))
            setSelectedMinute(format(deadlineDate, "mm"))
            setSelectedPeriod(format(deadlineDate, "a") as "AM" | "PM")
        }
    }, [settings])

    const handleSaveDeadline = async () => {
        if (!date) {
            toast.error("Please select a deadline date")
            return
        }

        try {
            // Convert 12-hour format to 24-hour for date-fns
            const hour =
                selectedPeriod === "PM" && selectedHour !== "12"
                    ? parseInt(selectedHour) + 12
                    : selectedPeriod === "AM" && selectedHour === "12"
                      ? 0
                      : parseInt(selectedHour)

            const deadlineWithTime = set(date, {
                hours: hour,
                minutes: parseInt(selectedMinute),
                seconds: 0,
                milliseconds: 0,
            })

            // Check if deadline is in the future
            if (deadlineWithTime <= new Date()) {
                toast.error("Deadline must be in the future")
                return
            }

            await updateSettings.mutateAsync({
                settings: {
                    deadline: Timestamp.fromDate(deadlineWithTime),
                },
                scheduleUpdate: true,
            })
            toast.success("Deadline updated successfully")
        } catch (error) {
            toast.error(
                "Failed to update deadline: " +
                    (error instanceof Error ? error.message : "Unknown error")
            )
        }
    }

    const handleToggleApplications = async (checked: boolean) => {
        try {
            await updateSettings.mutateAsync({
                settings: {
                    isAcceptingApplications: checked,
                },
            })
            toast.success(
                `Applications ${checked ? "opened" : "closed"} successfully`
            )
        } catch (error) {
            toast.error("Failed to update application status")
        }
    }

    const handleClearDeadline = async () => {
        try {
            await updateSettings.mutateAsync({
                settings: {
                    deadline: null,
                },
            })
            setDate(undefined)
            toast.success("Deadline cleared")
        } catch (error) {
            toast.error(
                "Failed to clear deadline: " +
                    (error instanceof Error ? error.message : "Unknown error")
            )
        }
    }

    if (isLoading) {
        return <div>Loading...</div>
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center space-x-4">
                <Label htmlFor="accept-applications">
                    Accepting new applications
                </Label>
                <Switch
                    id="accept-applications"
                    checked={settings?.isAcceptingApplications}
                    onCheckedChange={handleToggleApplications}
                />
            </div>

            <div className="space-y-2">
                <Label>Applications deadline</Label>

                {settings?.deadline && (
                    <div className="flex gap-2 items-center">
                        <p className="text-sm text-muted-foreground">
                            Current application deadline:{" "}
                            <span className="font-semibold text-app-defaultLight">
                                {format(
                                    settings.deadline.toDate(),
                                    "PPP 'at' p"
                                )}
                            </span>
                        </p>
                        {settings?.deadline && (
                            <Button
                                onClick={handleClearDeadline}
                                variant="link"
                                disabled={updateSettings.isPending}
                                className="text-red-500 underline h-6"
                            >
                                Clear Deadline
                            </Button>
                        )}
                    </div>
                )}

                {!settings?.deadline && (
                    <div className="flex flex-col justify-center items-start gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    disabled={
                                        settings?.isAcceptingApplications ===
                                        false
                                    }
                                    className={cn(
                                        "w-[240px] justify-start text-left font-normal",
                                        !date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {date ? (
                                        format(date, "PPP")
                                    ) : (
                                        <span>Pick a date</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0 z-[200]"
                                align="start"
                            >
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>

                        <div className="flex items-center gap-2">
                            <Select
                                value={selectedHour}
                                onValueChange={setSelectedHour}
                                disabled={
                                    settings?.isAcceptingApplications === false
                                }
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Hour" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 12 }, (_, i) =>
                                        (i + 1).toString().padStart(2, "0")
                                    ).map((hour) => (
                                        <SelectItem key={hour} value={hour}>
                                            {hour}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <span>:</span>

                            <Select
                                value={selectedMinute}
                                onValueChange={setSelectedMinute}
                                disabled={
                                    settings?.isAcceptingApplications === false
                                }
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Min" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 60 }, (_, i) =>
                                        i.toString().padStart(2, "0")
                                    ).map((minute) => (
                                        <SelectItem key={minute} value={minute}>
                                            {minute}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select
                                value={selectedPeriod}
                                onValueChange={(value) =>
                                    setSelectedPeriod(value as "AM" | "PM")
                                }
                                disabled={
                                    settings?.isAcceptingApplications === false
                                }
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="AM/PM" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AM">AM</SelectItem>
                                    <SelectItem value="PM">PM</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <Button
                            onClick={handleSaveDeadline}
                            disabled={
                                updateSettings.isPending ||
                                settings?.isAcceptingApplications === false ||
                                !date
                            }
                        >
                            Set Deadline
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}
